import { PEER_LEAVE_REASON, ASR_ROBOT_FROM_TYPE } from './../common/constants';
import { EventEmitter } from 'eventemitter3';
import { getMuteStateFromFlag, isUndefined } from '../utils/utils';
import { UserRoleNumber, User, UserRole, BasePublishedUser } from '../enums';
import Logger from '../utils/log/logger';

export const enum UserManagerEvent {
  PEER_JOIN = '1',
  PEER_LEAVE = '2',
  REMOTE_PUBLISHED = '3',
  REMOTE_UNPUBLISHED = '5',
  // 远端推流状态变更事件：包括远端推流、远端取消推流、远端推流变更(例如：从只推音频，变为推音视频)
  REMOTE_PUBLISH_STATE_CHANGED = '6',
  SELF_PUBLISHED = '7',
  ASR_ROBOT_PEER_JOIN = '8',
  ASR_ROBOT_PEER_LEAVE = '9'
}

export interface UserManagerEventTypes {
  [UserManagerEvent.PEER_JOIN]: [User];
  [UserManagerEvent.PEER_LEAVE]: [{userId: string, reason?: keyof typeof PEER_LEAVE_REASON}];
  [UserManagerEvent.REMOTE_PUBLISHED]: [BasePublishedUserInfo];
  [UserManagerEvent.REMOTE_UNPUBLISHED]: [userId: string];
  [UserManagerEvent.REMOTE_PUBLISH_STATE_CHANGED]: [{ prevMuteState: MuteState, muteState: MuteState; flag: number; }];
  [UserManagerEvent.SELF_PUBLISHED]: [BasePublishedUserInfo];
  [UserManagerEvent.ASR_ROBOT_PEER_JOIN]: [User];
  [UserManagerEvent.ASR_ROBOT_PEER_LEAVE]: [User];
}

/**
 * 用户管理模块
 *
 * 抛出 peer-join, peer-leave, remote-published, remote-unpublished, remote-published-state-changed 事件
 *
 * 注意：该模块在 WebRTC 中运行在主线程；在 WASM 中运行在 Worker 线程，无法使用例如 innerEmitter、room 等在主线程才有的能力。
 *
 * @export
 * @class UserManager
 * @extends {EventEmitter}
 * @template T
 */
export class UserManager<T extends BasePublishedUser> extends EventEmitter<UserManagerEventTypes> {
  userMap: Map<string, User> = new Map();
  remotePublishedUserMap: Map<string, T> = new Map();
  asrRobotUserMap: Map<string, User> = new Map();
  // 记录参与混流的 user
  get hasRobotUser() {
    return !![...this.remotePublishedUserMap.values()].find(remotePublishedUser => remotePublishedUser.isRobot);
  }

  constructor(public mySelfId: string, private _log: Logger, public key: keyof UserIds = 'userId') {
    super();
  }
  getPublishedUser(key: string) {
    return this.remotePublishedUserMap.get(key);
  }

  addUser(baseUserInfo: BaseUserInfo) {
    const userKey = baseUserInfo[this.key] as string;
    const { userId, tinyId, role, fromType } = baseUserInfo;
    if (fromType === ASR_ROBOT_FROM_TYPE) {
      this.addAsrRobotUser(baseUserInfo);
      return;
    }
    if (this.userMap.has(userKey)) return;
    const user = { userId, tinyId, role: role === UserRoleNumber.ANCHOR ? UserRole.ANCHOR : UserRole.AUDIENCE };
    this.userMap.set(userKey, user);
    this.emit(UserManagerEvent.PEER_JOIN, user);
  }

  addAsrRobotUser(baseUserInfo: BaseUserInfo) {
    const userKey = baseUserInfo[this.key] as string;
    const { userId, tinyId, role } = baseUserInfo;
    if (this.asrRobotUserMap.has(userKey)) return;
    const user = { userId, tinyId, role: role === UserRoleNumber.ANCHOR ? UserRole.ANCHOR : UserRole.AUDIENCE };
    this.asrRobotUserMap.set(userKey, user);
    this.emit(UserManagerEvent.ASR_ROBOT_PEER_JOIN, user);
  }

  deleteUser(userKey: string, reason?: keyof typeof PEER_LEAVE_REASON) {
    const user = this.userMap.get(userKey);
    if (!user) return;
    const isAsrRobot = this.asrRobotUserMap.has(userKey);
    if (isAsrRobot) {
      this.deleteAsrRobotUser(userKey);
      return;
    }
    let log = `peer leave [${userKey}]`;
    if (!isUndefined(reason)) {
      log += `:${PEER_LEAVE_REASON[reason as number]}`;
    }
    this._log.info(log);

    // 远端用户退房，先抛出 REMOTE_UNPUBLISHED 事件
    const remotePublishedUser = this.remotePublishedUserMap.get(userKey);
    if (remotePublishedUser) {
      const prevMuteState = remotePublishedUser.muteState;
      remotePublishedUser.flag = 0;
      this.emit(UserManagerEvent.REMOTE_UNPUBLISHED, remotePublishedUser.userId);
      this.deleteRemotePublishedUser(userKey);
      this.emit(UserManagerEvent.REMOTE_PUBLISH_STATE_CHANGED, { prevMuteState, muteState: remotePublishedUser.muteState, flag: 0 });
    }

    this.userMap.delete(userKey);
    this.emit(UserManagerEvent.PEER_LEAVE, { userId: user.userId, reason });
  }

  deleteAsrRobotUser(userKey: string) {
    if (!this.asrRobotUserMap.has(userKey)) return;
    const user = this.asrRobotUserMap.get(userKey);
    if (user) {
      this.asrRobotUserMap.delete(userKey);
      this.emit(UserManagerEvent.ASR_ROBOT_PEER_LEAVE, user);
    }
  }

  setUserList(userList: BaseUserInfo[]) {
    // 比对 userMap 和 后台下发的用户列表，清理已不在房间的用户
    // 场景：当断网时，远端用户退房，此时收不到后台的 peer-join peer-leave，
    // 因此在重连成功后，同步一次用户列表。
    this.userMap.forEach(user => {
      if (userList.findIndex(u => u[this.key] === user[this.key]) < 0) {
        this.deleteUser(user[this.key], 0);
      }
    });

    userList.forEach(user => {
      if (user[this.key] === this.mySelfId) return;
      if (!this.userMap.has(user[this.key])) {
        this.addUser(user);
      } else {
        // WHIP 拉流, WS rebuild 后通过 cmd 4137 同步用户列表时，更新为远端用户真实的 tinyId
        const existingUser = this.userMap.get(user[this.key])!;
        if (user.tinyId && existingUser.tinyId !== user.tinyId) {
          this._log.info(`setUserList: update [${user.userId}] tinyId: ${existingUser.tinyId} -> ${user.tinyId}`);
          existingUser.tinyId = user.tinyId;
          const remotePublishedUser = this.remotePublishedUserMap.get(user[this.key] as string);
          if (remotePublishedUser && remotePublishedUser.tinyId !== user.tinyId) {
            remotePublishedUser.tinyId = user.tinyId;
          }
        }
      }
    });
  }

  addRemotePublishedUser(remotePublishedUser: T) {
    if (this.remotePublishedUserMap.has(remotePublishedUser[this.key])) return;
    this.remotePublishedUserMap.set(remotePublishedUser[this.key], remotePublishedUser);
  }

  deleteRemotePublishedUser(userKey: string) {
    if (!this.remotePublishedUserMap.has(userKey)) return;
    this.remotePublishedUserMap.delete(userKey);
  }

  setRemotePublishedUserList(publishedUserList: BasePublishedUserInfo[]) {
    this.remotePublishedUserMap.forEach(remotePublishedUser => {
      const userKey = remotePublishedUser[this.key];
      // 检测到下行已取消推流
      if (publishedUserList.findIndex(publishUser => publishUser[this.key] === remotePublishedUser[this.key]) < 0) {
        this._log.info(`remote [${userKey}] unpublish`);
        const prevMuteState = remotePublishedUser.muteState;
        remotePublishedUser.flag = 0;
        this.emit(UserManagerEvent.REMOTE_UNPUBLISHED, remotePublishedUser.userId);
        this.deleteRemotePublishedUser(userKey);
        this.emit(UserManagerEvent.REMOTE_PUBLISH_STATE_CHANGED, { prevMuteState, muteState: remotePublishedUser.muteState, flag: 0 });
      }
    });

    publishedUserList.forEach(publishedUser => {
      const userKey = publishedUser[this.key];
      // 自己不处理
      if (userKey === this.mySelfId) {
        this.emit(UserManagerEvent.SELF_PUBLISHED, publishedUser);
        return;
      }
      const { flag, userId, tinyId, fromType } = publishedUser;
      const muteState = getMuteStateFromFlag(flag, userId);
      const prevMuteState = this.remotePublishedUserMap.get(userKey)?.muteState;

      if (prevMuteState) {
        const remotePublishedUser = this.remotePublishedUserMap.get(userKey);
        if (remotePublishedUser && remotePublishedUser.flag !== flag) {
          remotePublishedUser.flag = flag;
          this._log.info(`remote publish updated: ${JSON.stringify(remotePublishedUser.muteState)}`);
          this.emit(UserManagerEvent.REMOTE_PUBLISH_STATE_CHANGED, { prevMuteState, muteState, flag });
        }
      } else {
        this._log.info(`remote publish. state: ${JSON.stringify(muteState)}`);

        // 当远端用户推流时，若当前 userMap 没有该远端用户，则添加该 user，并抛出 peer-join
        this.addUser({ userId, tinyId, role: UserRoleNumber.ANCHOR, fromType });
        this.emit(UserManagerEvent.REMOTE_PUBLISHED, publishedUser);
        this.emit(UserManagerEvent.REMOTE_PUBLISH_STATE_CHANGED, { prevMuteState: getMuteStateFromFlag(0, userId), muteState, flag });
      }
    });
  }

  clear() {
    this.userMap.clear();
    this.remotePublishedUserMap.clear();
  }
}
